import React from "react";
import { useTranslate } from "@refinedev/core";
import { AlertCard } from "@libertify/ui";
import { Col, Empty, Row } from "antd";
import type { QuizCohort, QuizPreparation } from "types/";
import { AnalyticsBarChart } from "../AnalyticsBarChart";
import { AnalyticsCard } from "../AnalyticsCard";
import { analyticsTheme } from "../analyticsTheme";
import { formatPct } from "../utils/formatPct";
import { barChartOptions, chartPlotHeight, hbarPlotHeight } from "./utils/chartSizing";
import { HeatTable, type HeatCell, type HeatRow } from "../HeatTable";
import { ItemRows, type ItemRow } from "../ItemRows";
import { ADDON_COLORS, LOW_SAMPLE } from "./utils/metrics";
import {
  cohortLabel,
  cohortScore,
  deltaShade,
  prepDelta,
  prepGrid,
} from "./utils/preparation";

const T_PREFIX = "media.projects.analytics.addons";
const QUIZ_COLOR = ADDON_COLORS.quiz;
const GREY = analyticsTheme.datasetGrey;
const BUCKETS = ["0", "1", "2", "3+"];

export type QuizPreparationSectionProps = {
  preparation: QuizPreparation | null;
  /** The preparation request failed: say so rather than "nobody finished". */
  preparationFailed?: boolean;
};

export const QuizPreparationSection: React.FC<QuizPreparationSectionProps> = ({
  preparation: prep,
  preparationFailed = false,
}) => {
  const t = useTranslate();

  if (!prep)
    return preparationFailed ? (
      <AlertCard type="error" title={t(`${T_PREFIX}.loadError`)} />
    ) : (
      // Nobody finished the quiz.
      <Empty description={t(`${T_PREFIX}.empty.noTakers`)} />
    );

  const name = (key: string) => t(`${T_PREFIX}.names.${key}`);
  const noViewer = t(`${T_PREFIX}.empty.noViewer`);

  const cohorts = [
    prep.quiz_first,
    ...[...prep.prior_addons].sort(
      (a, b) => (b.avg_score ?? 0) - (a.avg_score ?? 0)
    ),
    ...prep.main_content,
  ];
  const buckets = BUCKETS.map((k) =>
    prep.prior_addon_counts.find((c) => c.cohort === k)
  );
  const engaged = prep.engaged_prior_addons;
  const grid = prepGrid(prep.questions);
  const showEngaged = engaged.some(
    (e) => e.used.viewers > 0 || e.not_used.viewers > 0
  );

  // Score on the right with the viewer count, as in "Correct answers per question".
  const scoreRow = (
    key: string,
    text: string,
    cohort: QuizCohort | undefined
  ): ItemRow => {
    const score = cohortScore(cohort);
    const viewers = cohort?.viewers ?? 0;
    return {
      key,
      text,
      value:
        score == null ? (
          noViewer
        ) : (
          <>
            <strong>{formatPct(score)}</strong> · {viewers.toLocaleString()}
          </>
        ),
      tooltip:
        score == null
          ? noViewer
          : t(`${T_PREFIX}.prep.rowTooltip`, {
              count: viewers,
              pct: formatPct(score),
            }),
      bar: score == null ? null : { percent: score, color: QUIZ_COLOR },
    };
  };
  const cohortRows = cohorts.map((c) => scoreRow(c.cohort, name(c.cohort), c));

  const heatRows: HeatRow[] = grid.rows.map((row) => {
    const n = t(`${T_PREFIX}.itemNumber.quiz`, { n: row.item_index + 1 });
    const label = row.label ?? t(`${T_PREFIX}.itemFallback`, { n });
    return {
      key: row.key,
      header: (
        <>
          <strong>{n}</strong> {label}
        </>
      ),
      title: `${n} ${label}`,
      cells: grid.addons.map((addon): HeatCell | null => {
        const d = prepDelta(prep.questions, row.item_index, addon);
        if (!d) return null;
        return {
          ...deltaShade(d.delta),
          text: `${d.delta >= 0 ? "+" : ""}${d.delta}`,
          faded: d.low,
          tooltip: t(`${T_PREFIX}.prep.heatTooltip`, {
            usedCorrect: d.used.correct,
            usedAnswers: d.used.answers,
            notCorrect: d.notUsed.correct,
            notAnswers: d.notUsed.answers,
          }),
        };
      }),
    };
  });

  const comparable = heatRows.some((row) =>
    row.cells.some((cell) => cell != null)
  );

  const renderHeat = () => {
    if (grid.rows.length === 0)
      return (
        <Empty
          description={t(`${T_PREFIX}.empty.noQuestions`, { count: prep.quiz_takers })}
        />
      );
    // Each cell needs takers on both sides: with only quiz-first
    // takers (or only add-on users) there is nothing to compare yet.
    if (!comparable) return <Empty description={t(`${T_PREFIX}.empty.noComparison`)} />;
    return <HeatTable columns={grid.addons.map(name)} rows={heatRows} />;
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <AnalyticsCard
          title={t(`${T_PREFIX}.prep.cohortsTitle`)}
          subtitle={t(`${T_PREFIX}.prep.cohortsSubtitle`, {
            count: prep.quiz_takers,
          })}
          info={t(`${T_PREFIX}.prep.cohortsInfo`, { min: LOW_SAMPLE })}
        >
          <ItemRows rows={cohortRows} />
        </AnalyticsCard>
      </Col>
      <Col xs={24} lg={showEngaged ? 12 : 24}>
        <AnalyticsBarChart
          title={t(`${T_PREFIX}.prep.doseTitle`)}
          subtitle={t(`${T_PREFIX}.prep.doseSubtitle`)}
          labels={BUCKETS.map((k, i) =>
            cohortLabel(
              t(`${T_PREFIX}.prep.bucket.${k}`),
              buckets[i],
              (count) => t(`${T_PREFIX}.quiz.viewersCount`, { count }),
              noViewer
            )
          )}
          datasets={[
            {
              label: t(`${T_PREFIX}.prep.avgScore`),
              data: buckets.map(cohortScore),
              color: QUIZ_COLOR,
            },
          ]}
          direction="vertical"
          showValueLabels
          plotHeight={chartPlotHeight(220)}
          categoryPercentage={0.9}
          barPercentage={0.85}
        />
      </Col>
      {/* Only drawn when at least one engaged group has takers. */}
      {showEngaged ? (
        <Col xs={24} lg={12}>
          <AnalyticsBarChart
            title={t(`${T_PREFIX}.prep.engagedTitle`)}
            subtitle={t(`${T_PREFIX}.prep.engagedSubtitle`)}
            info={t(`${T_PREFIX}.prep.engagedInfo`)}
            labels={engaged.map((e) => name(e.addon))}
            datasets={[
              {
                label: t(`${T_PREFIX}.prep.used`),
                data: engaged.map((e) => cohortScore(e.used)),
                color: QUIZ_COLOR,
              },
              {
                label: t(`${T_PREFIX}.prep.notUsed`),
                data: engaged.map((e) => cohortScore(e.not_used)),
                color: GREY,
              },
            ]}
            direction="horizontal"
            showValueLabels
            plotHeight={hbarPlotHeight(engaged.length, 2)}
            chartOptions={barChartOptions}
          />
        </Col>
      ) : null}
      <Col span={24}>
        <AnalyticsCard
          title={t(`${T_PREFIX}.prep.heatTitle`)}
          subtitle={t(`${T_PREFIX}.prep.heatSubtitle`)}
          info={t(`${T_PREFIX}.prep.heatInfo`)}
        >
          {renderHeat()}
        </AnalyticsCard>
      </Col>
    </Row>
  );
};
